import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useMedia } from 'react-use'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import Plyr from 'plyr'

import 'plyr/dist/plyr.css'

import { MediaVideoParams } from './MediaVideo'

export type MediaPlayerProps = MediaVideoParams
export type MediaPlayerHandler = { play: () => void }

export const MediaPlayer = forwardRef<MediaPlayerHandler, MediaPlayerProps>((props, ref) => {
  const targetRef = useRef<HTMLDivElement>(null!)
  const videoRef = useRef<HTMLVideoElement>(null!)
  const player = useRef<Plyr>(null!)
  const isMobile = useMedia('(max-width: 767px)', true)
  const initialized = useRef(false)

  const source = useMemo(() => {
    const { video_provider, video_mobile, video_desktop, video_url_mobile, video_url_desktop } =
      props

    const isFile = video_provider === 'file'
    const mobileSrc = isFile
      ? video_mobile?.url || video_desktop?.url
      : video_url_mobile || video_url_desktop
    const desktopSrc = isFile ? video_desktop?.url : video_url_desktop

    return isMobile ? mobileSrc : desktopSrc
  }, [isMobile, props])

  const initializePlyr = useCallback(async () => {
    const PlyrModule = await import('plyr')
    const Plyr = PlyrModule.default
    const target = videoRef.current || targetRef.current

    player.current = new Plyr(target, {
      loop: { active: props.autoplay },
      autoplay: props.autoplay,
      muted: props.autoplay
    })

    initialized.current = true
  }, [props.autoplay])

  /* useEffect(() => {
    if (initialized.current) {
      initializePlyr()
    }

    return () => {
      if (player.current) {
        console.log('DESTROY')
        player.current.destroy()
        player.current = null!
      }
    }
  }, [source, initializePlyr]) */

  const playVideo = () => {
    if (player.current) {
      player.current.play()
    }
  }

  const stopVideo = () => {
    if (player.current) {
      player.current.pause()
    }
  }

  useEffect(() => {
    const scrollTrigger = ScrollTrigger.create({
      trigger: videoRef.current || targetRef.current,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      markers: false,
      onEnter: initializePlyr,
      onEnterBack: playVideo,
      onLeave: stopVideo,
      onLeaveBack: stopVideo
    })

    return () => {
      scrollTrigger?.kill()
    }
  }, [initializePlyr])

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        console.log('play')
      }
    }),
    []
  )

  return (
    <>
      <style jsx global>{`
        :root {
          --plyr-color-main: #8faab2;
        }
      `}</style>

      {props.video_provider === 'file' || props.video_provider === 'url' ? (
        <video ref={videoRef} src={source} />
      ) : (
        <div
          ref={targetRef}
          className="aspect-video"
          data-plyr-provider={props.video_provider}
          data-plyr-embed-id={source}
        />
      )}
    </>
  )
})

export default MediaPlayer
